import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiBaseService } from './api-base.service';
import { ConfigTrafficLightRequestModel, ConfigTrafficLightResultModel } from '../interfaces/entities/traffic-light';
import { EmailReportConfigRequestModel, EmailReportConfigResultModel } from '../interfaces/entities/email-report';
import { AppGlobals } from '../app.globals';
import { TaskTagCatalogItem } from '../interfaces/entities/task-tag';
import {
  GetProjectConfigurationResultModel,
  GetProjectConfigurationStatusResultModel,
} from '../interfaces/entities/project-configuration';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  public userList$ = new BehaviorSubject<any[]>([]);
  public userMap$ = new BehaviorSubject<{ [key: string]: string }>({});

  public contactTags$ = new BehaviorSubject<any[]>([]);
  private isContactTagsLoading = false;

  public taskTags$ = new BehaviorSubject<TaskTagCatalogItem[]>([]);
  private taskTagsRequest: Promise<TaskTagCatalogItem[]> | null = null;

  // State management for project configuration
  public projectConfiguration$ = new BehaviorSubject<GetProjectConfigurationResultModel | null>(null);
  private isProjectConfigurationLoading = false;

  public projectConfigStatus$ = new BehaviorSubject<GetProjectConfigurationStatusResultModel | null>(null);
  public isProjectConfigStatusLoading = false;

  private isUserListLoading = false;

  public setupCancelSubject = new Subject<void>();
  setupCancel$ = this.setupCancelSubject.asObservable();

  emitSetupCancel() {
    this.setupCancelSubject.next();
  }

  constructor(private apiService: ApiBaseService) {}

  /************** User Roles **************/
  getUsers(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetUsers', payLoad, true, true);
  }

  async getListOfUsers(payLoad: any): Promise<any> {
    const result = await this.apiService.httpRequest('ConfigService/GetListUsers', payLoad, true, true);

    // Update the BehaviorSubject with the user list
    if (!result.hasError && result.projectsUsers) {
      const formattedUsers = result.projectsUsers.map((user: any) => ({
        displayName: user.displayName,
        emailAddress: user.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        wiEnergyEntraOid: user.wiEnergyEntraOid,
        role: user.role,
      }));

      this.userList$.next(formattedUsers);
      this.userMap$.next(
        formattedUsers.reduce(
          (acc, user) => {
            acc[user.emailAddress] = user.displayName;
            return acc;
          },
          {} as { [key: string]: string }
        )
      );
    }

    return result;
  }

  async loadUserList(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.userList$.value.length > 0) {
      return this.userList$.value;
    }

    if (this.isUserListLoading) {
      return this.userList$.value;
    }

    this.isUserListLoading = true;
    try {
      const result = await this.getListOfUsers({});
      return this.userList$.value;
    } finally {
      this.isUserListLoading = false;
    }
  }

  getAvailableUsers(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetAvailableUsers', payLoad, true, true);
  }

  writeUsers(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteUsers', payLoad, true, true);
  }

  writeUserPermissions(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteUserRoles', payLoad, true, true);
  }

  deleteUser(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteUser', payload, true, true);
  }

  confirmDeleteUser(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/ConfirmDeleteUser', payload, true, true);
  }

  getEpcsAndPartners(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetEpcsAndPartners', payload, true, true);
  }

  /************** Contact Tags **************/
  async getContactTags(payLoad: any): Promise<any> {
    const result = await this.apiService.httpRequest('ConfigService/GetContactTags', payLoad, true, true);

    if (!result.hasError && result.contactTags) {
      this.contactTags$.next(result.contactTags);
    }

    return result;
  }

  async loadContactTags(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.contactTags$.value.length > 0) {
      return this.contactTags$.value;
    }

    if (this.isContactTagsLoading) {
      return this.contactTags$.value;
    }

    this.isContactTagsLoading = true;
    try {
      await this.getContactTags({});
      return this.contactTags$.value;
    } finally {
      this.isContactTagsLoading = false;
    }
  }

  writeContactTags(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteContactTags', payLoad, true, true);
  }

  deleteContactTag(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteContactTag', payLoad, true, true);
  }

  /************** Task Tags (#4752) **************/
  async getTaskTags(): Promise<{ hasError: boolean; feedback?: string; taskTags: TaskTagCatalogItem[] }> {
    const result = await this.apiService.httpRequest('ConfigService/GetTaskTags', {}, true, true);
    if (!result.hasError && result.taskTags) {
      this.taskTags$.next(result.taskTags);
    }
    return result;
  }

  /** Shared catalog for the many task-definition nodes; one request however many subscribe. */
  loadTaskTags(forceReload = false): Promise<TaskTagCatalogItem[]> {
    if (!forceReload && this.taskTags$.value.length > 0) {
      return Promise.resolve(this.taskTags$.value);
    }
    this.taskTagsRequest ??= this.getTaskTags()
      .then(() => this.taskTags$.value)
      .finally(() => (this.taskTagsRequest = null));
    return this.taskTagsRequest;
  }

  writeTaskTag(taskTag: { id: number | null; name: string }): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteTaskTag', { taskTag }, true, true);
  }

  deleteTaskTag(id: number): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteTaskTag', { taskTag: { id } }, true, true);
  }


  /************** Dashboard Config **************/
  getDashboardConfig(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetDashboardConfig', payLoad, true, false);
  }

  writeDashboardConfig(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteDashboardConfig', payLoad, true, false);
  }

  /************** Dashboard Config **************/
  getWikiConfig(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetWikiConfig', payLoad, true, false);
  }

  writeWikiConfig(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteWikiConfig', payLoad, true, false);
  }

  async getMilestones(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetMilestones', payload, true, true);
  }

  async writeMilestoneConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteMilestoneConfig', payload, true, true);
  }

  async deleteMilestone(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteMilestone', payload, true, true);
  }

  async getExportConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetExportConfig', payload, true, true);
  }

  async saveExportConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SaveExportConfig', payload, true, true);
  }

  async writeProjectAreaTypes(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteProjectAreaTypes', payload, true, true);
  }

  async getExportPackageData(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetExportPackageData', payload, true, true);
  }

  async deleteExportConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteExportConfig', payload, true, true);
  }

  async getProjectOverviewConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetProjectOverviewConfig', payload, true, true);
  }

  async saveProjectOverviewConfig(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SaveProjectOverviewConfig', payload, true, true);
  }

  async deletePhase(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeletePhase', payload, true, true);
  }

  async writePhase(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WritePhase', payload, true, true);
  }

  async writeMilestone(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteMilestone', payload, true, true);
  }

  async writeTaskPackage(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WriteTaskPackage', payload, true, true);
  }

  async getTaskPackageArchiveImpact(packageId: number): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetTaskPackageArchiveImpact', { packageId }, true, true);
  }

  async archiveTaskPackage(packageId: number): Promise<any> {
    return this.apiService.httpRequest('ConfigService/ArchiveTaskPackage', { packageId }, true, true);
  }

  async unarchiveTaskPackage(packageId: number): Promise<any> {
    return this.apiService.httpRequest('ConfigService/UnarchiveTaskPackage', { packageId }, true, true);
  }

  async unarchiveTaskPackageOnly(packageId: number): Promise<any> {
    return this.apiService.httpRequest('ConfigService/UnarchiveTaskPackageOnly', { packageId }, true, true);
  }

  /************** Project Config **************/

  async saveProjectSetup(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SetupProjectConfiguration', payload, true, true);
  }

  async loadProjectConfigStatus(
    forceReload: boolean = false
  ): Promise<GetProjectConfigurationStatusResultModel | null> {
    if (!forceReload && this.projectConfigStatus$.value !== null) {
      return this.projectConfigStatus$.value;
    }

    if (this.isProjectConfigStatusLoading) {
      return this.projectConfigStatus$.value;
    }

    this.isProjectConfigStatusLoading = true;
    try {
      const result = await this.getProjectConfigStatus({});
      return this.projectConfigStatus$.value;
    } finally {
      this.isProjectConfigStatusLoading = false;
    }
  }

  async getProjectConfigStatus(payload: any): Promise<GetProjectConfigurationStatusResultModel> {
    const result = await this.apiService.httpRequest('ConfigService/GetProjectConfigStatus', payload, true, true);

    if (!result.hasError) {
      this.projectConfigStatus$.next(result);
    }

    return result;
  }

  async getProjectConfiguration(showLoader = true): Promise<GetProjectConfigurationResultModel> {
    const result = await this.apiService.httpRequest('ConfigService/GetProjectConfig', {}, true, showLoader);

    // Update project configuration state if available
    if (!result.hasError) {
      this.projectConfiguration$.next(result);
    }

    return result;
  }

  async loadProjectConfiguration(forceReload: boolean = false): Promise<GetProjectConfigurationResultModel | null> {
    if (!forceReload && this.projectConfiguration$.value !== null) {
      return this.projectConfiguration$.value;
    }

    if (this.isProjectConfigurationLoading) {
      return this.projectConfiguration$.value;
    }

    this.isProjectConfigurationLoading = true;
    try {
      const result = await this.getProjectConfiguration();
      return this.projectConfiguration$.value;
    } finally {
      this.isProjectConfigurationLoading = false;
    }
  }

  /************** Traffic light Config **************/
  async getTrafficLightConfig(): Promise<ConfigTrafficLightResultModel> {
    return this.apiService.httpRequest('ConfigService/GetTrafficLightConfig', {}, true, true);
  }

  async saveTrafficLightConfig(payload: ConfigTrafficLightRequestModel): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SaveTrafficLightConfig', payload, true, true);
  }

  /************** Email Report Config **************/
  async getEmailReportConfig(): Promise<EmailReportConfigResultModel> {
    return this.apiService.httpRequest('ConfigService/GetEmailReportConfig', {}, true, true);
  }

  async saveEmailReportConfig(payload: EmailReportConfigRequestModel): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SaveEmailReportConfig', payload, true, true);
  }

  async exportDatabase(): Promise<any> {
    return this.apiService.httpRequest('ConfigService/ExportDatabase', {}, true, true);
  }
  async getDatabaseExportList(): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetDatabaseExportList', {}, true, false);
  }

  async deleteDatabaseExport(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteDatabaseExport', payload, true, true);
  }

  async getPropertyDocumentTypes(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetPropertyDocumentTypes', payload, true, true);
  }

  async savePropertyDocumentType(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WritePropertyDocumentType', payload, true, true);
  }

  async deletePropertyDocumentType(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeletePropertyDocumentType', payload, true, true);
  }

  async getPropertyAreaTypes(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetPropertyAreaTypes', payload, true, true);
  }

  async writePropertyAreaType(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/WritePropertyAreaType', payload, true, true);
  }

  async deletePropertyAreaType(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeletePropertyAreaType', payload, true, true);
  }

  async getOrganizationStructure(): Promise<any> {
    return this.apiService.httpRequest('ConfigService/GetOrganizationStructure', {}, true, true);
  }

  async saveOrganizationStructureItem(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/SaveOrganizationStructureItem', payload, true, true);
  }

  async deleteOrganizationStructureItem(payload: any): Promise<any> {
    return this.apiService.httpRequest('ConfigService/DeleteOrganizationStructureItem', payload, true, true);
  }
}
